import { Component, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { UtilsService } from 'src/app/services/utils.service';
import { MaintenanceService } from 'src/app/services/maintenance.service';
import { forkJoin } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { PriorityService } from 'src/app/services/priority.service';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import Swal from 'sweetalert2';
import { UnitService } from 'src/app/services/unit.service';

interface SelectedRepair {
    id: number
    name: string
    category: string
    unit_cost: number
    quantity: number
    in_stock: boolean
}

interface SelectedMaterial {
    id: number
    name: string
    cost: number
    in_stock: boolean
}

@Component({
    selector: 'app-maintenance-add',
    templateUrl: './maintenance-add.component.html',
    styleUrls: ['./maintenance-add.component.css']
})
export class MaintenanceAddComponent {

    unit: any = {}
    dataUser: any = {}
    clientSelected: any = { origin_address: [], destination_address: [] }

    materials: any[] = []
    repairs: any[] = []
    repairCategories: any[] = []

    // Refacciones
    selectedRepairs: SelectedRepair[] = []
    spareSearchTerm: string = ''
    filteredRepairs: any[] = []
    showSpareDropdown: boolean = false
    showAddRepairModal: boolean = false
    newRepair: { name: string; quantity: number; category: string; unit_cost: number | null; provider: string; description: string } = {
        name: '', quantity: 1, category: '', unit_cost: null, provider: '', description: ''
    }
    spareCategories: string[] = []

    // Modal agregar categoría
    showAddCategoryModal: boolean = false
    newCategoryName: string = ''
    showCategoryToast: boolean = false

    // Modal agregar falla
    showAddFailureModal: boolean = false
    newFailure: { failure_name: string; type_name: string; detail_name: string } = {
        failure_name: '', type_name: '', detail_name: ''
    }

    // AG Grid spare parts
    spareGridApi: any = null
    spareColDefs: any[] = []
    spareDefaultColDef: any = { sortable: false, filter: false, resizable: false, flex: 1 }

    // Materiales (inventario)
    selectedMaterials: SelectedMaterial[] = []
    materialSearchTerm: string = ''
    filteredMaterials: any[] = []
    showMaterialDropdown: boolean = false
    materialGridApi: any = null
    materialColDefs: any[] = []
    materialDefaultColDef: any = { sortable: false, filter: false, resizable: false, flex: 1 }

    get totalMaterialsAmount(): number {
        return this.selectedMaterials.reduce((sum, m) => sum + (m.cost || 0), 0)
    }

    onMaterialSearch(event: Event) {
        const input = event.target as HTMLInputElement
        this.materialSearchTerm = input.value
        const term = this.materialSearchTerm.trim().toLowerCase()
        if (!term) {
            this.filteredMaterials = []
            this.showMaterialDropdown = false
            return
        }
        this.filteredMaterials = this.materials
            .filter(m => m.name?.toLowerCase().includes(term) && !this.selectedMaterials.some(s => s.id === m.id))
            .slice(0, 10)
        this.showMaterialDropdown = this.filteredMaterials.length > 0
    }

    hideMaterialDropdown() {
        setTimeout(() => { this.showMaterialDropdown = false }, 150)
    }

    selectMaterial(material: any) {
        if (this.selectedMaterials.some(s => s.id === material.id)) return
        this.selectedMaterials = [...this.selectedMaterials, {
            id: material.id,
            name: material.name,
            cost: 0,
            in_stock: true
        }]
        this.materialSearchTerm = ''
        this.filteredMaterials = []
        this.showMaterialDropdown = false
        this.materialGridApi?.setRowData(this.selectedMaterials)
    }

    onMaterialGridReady(params: any) {
        this.materialGridApi = params.api
    }

    private buildMaterialColDefs() {
        this.materialColDefs = [
            {
                headerName: 'Material',
                field: 'name',
                flex: 1,
                cellRenderer: (p: any) => {
                    const div = document.createElement('div')
                    div.innerHTML = `<strong>${p.value ?? ''}</strong>`
                    return div
                }
            },
            {
                headerName: 'Costo',
                field: 'cost',
                flex: 1,
                cellRenderer: (p: any) => {
                    const input = document.createElement('input')
                    input.type = 'number'
                    input.min = '0'
                    input.step = '0.01'
                    input.placeholder = '0.00'
                    input.value = p.data.cost ? String(p.data.cost) : ''
                    input.className = 'form-control form-control-sm spare-cost-input'
                    input.addEventListener('change', () => {
                        const cost = parseFloat(input.value)
                        p.data.cost = !isNaN(cost) && cost >= 0 ? cost : 0
                    })
                    return input
                }
            },
            {
                headerName: '¿Es de inventario?',
                field: 'in_stock',
                flex: 1,
                cellRenderer: (p: any) => {
                    const input = document.createElement('input')
                    input.type = 'checkbox'
                    input.className = 'form-check-input'
                    input.checked = p.data.in_stock ?? true
                    input.addEventListener('change', () => { p.data.in_stock = input.checked })
                    return input
                }
            },
            {
                headerName: 'Acciones',
                field: 'id',
                flex: 1,
                cellRenderer: (p: any) => {
                    const btn = document.createElement('button')
                    btn.type = 'button'
                    btn.className = 'btn btn-link text-danger p-0'
                    btn.innerHTML = '<i class="fas fa-trash-alt"></i>'
                    btn.onclick = () => {
                        this.selectedMaterials = this.selectedMaterials.filter(m => m.id !== p.data.id)
                        this.materialGridApi?.setRowData(this.selectedMaterials)
                    }
                    return btn
                }
            }
        ]
    }

    onFilesSelected(event: Event) {
        const input = event.target as HTMLInputElement
        const files = input?.files ? Array.from(input.files) : []
        if (!files.length) return
        const toAdd = files.filter(f => !this.selectedFiles.some(e => (
            e.name === f.name && e.size === f.size && e.type === f.type && e.lastModified === f.lastModified
        )))
        if (!toAdd.length) { input.value = ''; return }
        this.selectedFiles = [...this.selectedFiles, ...toAdd]
        const newPreviews = toAdd.map(f => f.type?.startsWith('image/') ? URL.createObjectURL(f) : null)
        this.selectedPreviews = [...this.selectedPreviews, ...newPreviews]
        this.form.patchValue({ evidences: this.selectedFiles })
        this.form.get('evidences')?.updateValueAndValidity({ emitEvent: false })
        input.value = ''
    }

    failerus: any[] = []
    priority: any[] = []
    maintenanceType: any[] = []

    selectedFailureId: number | null = null
    selectedTypeId: number | null = null
    typesByFailure: any[] = []
    detailsByType: any[] = []
    now: Date = new Date()
    private _clock?: any
    text_loading = "Espere un momento..."
    selectedFiles: File[] = []
    selectedPreviews: (string | null)[] = []
    showRemoveModal = false
    indexToRemove: number | null = null

    private evidenceRequired: ValidatorFn = (control: AbstractControl) => {
        const val = control.value
        return Array.isArray(val) && val.length > 0 ? null : { requiredEvidence: true }
    }


    constructor(
        private location: Location,
        private utils: UtilsService,
        private apiMaintenance: MaintenanceService,
        private spinner: NgxSpinnerService,
        private apiPriority: PriorityService,
        private fb: FormBuilder,
        private router: Router,
        private apiUnits: UnitService,
        private cdr: ChangeDetectorRef
    ) { }

    form!: FormGroup
    submitting = false
    units: any[] = []

    private pad2(n: number) { return n < 10 ? `0${n}` : `${n}` }
    private formatDate(d: Date) { return `${d.getFullYear()}-${this.pad2(d.getMonth()+1)}-${this.pad2(d.getDate())}` }
    private formatTime(d: Date) { return `${this.pad2(d.getHours())}:${this.pad2(d.getMinutes())}` }
    private toInt(val: any): number | null {
        if (val === '' || val === null || val === undefined) return null
        const n = Number(val)
        return Number.isNaN(n) ? null : n
    }

    onChangeUnit(id: number) {
        this.unit = this.units.find((u: any) => u.id == id)
    }

    ngOnInit(): void {
        this.spinner.show()
        this.dataUser = this.utils.getDataUser()

        forkJoin([
            this.apiMaintenance.getData(),
            this.apiPriority.getData(),
            this.apiMaintenance.getMaintenanceType(),
            this.apiMaintenance.getRepairs(),
            this.apiMaintenance.getMaterials(),
            this.apiUnits.listUnit(),
            this.apiMaintenance.getRepairCategories()
        ]).subscribe(([resp1, resp2, resp3, resp4, resp5, resp6, resp7]) => {
            this.failerus = (resp1 as any[]) || []
            this.priority = (resp2 as any[]) || []
            this.maintenanceType = (resp3 as any[]) || []
            this.repairs = ((resp4 as any)?.data as any[]) || []
            this.materials = ((resp5 as any)?.data as any[]) || []
            this.units = ((resp6 as any) as any[]) || []
            this.repairCategories = ((resp7 as any)?.data as any[]) || []
            this.spareCategories = [...new Set(this.repairs.map((r: any) => r.category).filter(Boolean))]
            this.spinner.hide()
        })

        this.form = this.fb.group({
            request_date: [this.formatDate(this.now)],
            request_hour: [this.formatTime(this.now)],
            user_id: [this.dataUser?.id || ''],
            unit_id: ['', Validators.required],
            maintenance_failure_id: [''],
            maintenance_failure_type_id: [{ value: '', disabled: true }],
            maintenance_detail_id: [{ value: '', disabled: true }],
            maintenance_type_id: [''],
            priority_id: ['', Validators.required],
            required_material: [false],
            required_repair: [false],
            required_labor: [false],
            labor_cost: [null],
            labor_description: [''],
            estimated_time_repair_start: [null],
            estimated_time_repair_end: [null],
            km_inicial: [null],
            km_next_maintenance: [null],
            evidences: [[], [this.evidenceRequired]],
        })

        this.form.get('priority_id')?.valueChanges.subscribe(val => {
            this.updateValidatorsForPriority(val)
        })

        this.form.get('maintenance_type_id')?.valueChanges.subscribe((val) => {
            const selected = this.maintenanceType.find((t: any) => String(t?.id) === String(val))
            const isInternal = String((selected?.name ?? '')).trim().toUpperCase() === 'INTERNO'
            if (!isInternal) {
                this.form.get('required_material')?.setValue(false)
                this.form.get('required_repair')?.setValue(false)
            }
            this.form.get('estimated_time_repair_start')?.updateValueAndValidity({ emitEvent: false })
            this.form.get('estimated_time_repair_end')?.updateValueAndValidity({ emitEvent: false })
        })

        this.form.get('required_repair')?.valueChanges.subscribe((val) => {
            if (val !== true && String(val) !== 'true') {
                this.selectedRepairs = []
                this.spareSearchTerm = ''
                this.filteredRepairs = []
                this.showSpareDropdown = false
                this.spareGridApi?.setRowData([])
            }
        })

        this.form.get('required_material')?.valueChanges.subscribe((val) => {
            if (val !== true && String(val) !== 'true') {
                this.selectedMaterials = []
                this.materialSearchTerm = ''
                this.filteredMaterials = []
                this.showMaterialDropdown = false
                this.materialGridApi?.setRowData([])
            }
        })

        this.buildSpareColDefs()
        this.buildMaterialColDefs()

    }

    // --- Spare parts (refacciones) ---

    onSpareSearch(event: Event) {
        const input = event.target as HTMLInputElement
        this.spareSearchTerm = input.value
        const term = this.spareSearchTerm.trim().toLowerCase()
        if (!term) {
            this.filteredRepairs = []
            this.showSpareDropdown = false
            return
        }
        this.filteredRepairs = this.repairs
            .filter(r => r.name?.toLowerCase().includes(term) && !this.selectedRepairs.some(s => s.id === r.id))
            .slice(0, 10)
        this.showSpareDropdown = this.filteredRepairs.length > 0
    }

    hideSpareDropdown() {
        setTimeout(() => { this.showSpareDropdown = false }, 150)
    }

    selectSpare(repair: any) {
        if (this.selectedRepairs.some(s => s.id === repair.id)) return
        this.selectedRepairs = [...this.selectedRepairs, {
            id: repair.id,
            name: repair.name,
            category: repair.category || '',
            unit_cost: +(repair.unit_cost ?? repair.cost ?? 0),
            quantity: 1,
            in_stock: true
        }]
        this.spareSearchTerm = ''
        this.filteredRepairs = []
        this.showSpareDropdown = false
        this.spareGridApi?.setRowData(this.selectedRepairs)
    }

    get totalRepairsAmount(): number {
        return this.selectedRepairs.reduce((sum, r) => sum + r.unit_cost * r.quantity, 0)
    }

    openAddRepairModal() {
        this.newRepair = { name: '', quantity: 1, category: '', unit_cost: null, provider: '', description: '' }
        this.showAddRepairModal = true
    }

    confirmAddRepair() {
        if (!this.newRepair.name.trim() || !this.newRepair.unit_cost) return

        const payload = {
            name: this.newRepair.name.trim(),
            category: this.newRepair.category?.trim() || '',
            unit_cost: +(this.newRepair.unit_cost),
            provider: this.newRepair.provider?.trim() || '',
            description: this.newRepair.description?.trim() || ''
        }

        this.spinner.show()
        this.apiMaintenance.createRepair(payload).subscribe({
            next: (resp: any) => {
                const saved = { id: resp?.data?.id ?? resp?.id ?? Date.now(), ...payload }

                this.repairs = [...this.repairs, saved]
                if (saved.category && !this.spareCategories.includes(saved.category)) {
                    this.spareCategories = [...this.spareCategories, saved.category]
                }

                this.selectedRepairs = [...this.selectedRepairs, {
                    id: saved.id,
                    name: saved.name,
                    category: saved.category,
                    unit_cost: saved.unit_cost,
                    quantity: Math.max(1, this.newRepair.quantity || 1),
                    in_stock: true
                }]
                this.spareGridApi?.setRowData(this.selectedRepairs)
                this.showAddRepairModal = false
            },
            error: () => {
                Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo guardar la refacción' })
            },
            complete: () => this.spinner.hide()
        })
    }

    onSpareGridReady(params: any) {
        this.spareGridApi = params.api
    }

    private buildSpareColDefs() {
        this.spareColDefs = [
            {
                headerName: 'Refacción',
                field: 'name',
                flex: 1,
                headerClass: 'spare-left',
                cellRenderer: (p: any) => {
                    const div = document.createElement('div')
                    div.style.width = '100%'
                    div.innerHTML = `<strong>${p.value ?? ''}</strong>`
                    return div
                }
            },
            {
                headerName: 'Categoría',
                field: 'category',
                flex: 1,
                valueFormatter: (p: any) => p.value?.trim() ? p.value : 'Sin categoría'
            },
            {
                headerName: 'Costo unitario',
                field: 'unit_cost',
                flex: 1,
                cellRenderer: (p: any) => {
                    const val = p.data.unit_cost
                    if (val != null && val !== '' && +val > 0) {
                        return `$${(+val).toFixed(2)} MXN`
                    }
                    const input = document.createElement('input')
                    input.type = 'number'
                    input.min = '0'
                    input.step = '0.01'
                    input.placeholder = '0.00'
                    input.className = 'form-control form-control-sm spare-cost-input'
                    input.addEventListener('change', () => {
                        const cost = parseFloat(input.value)
                        if (!isNaN(cost) && cost > 0) {
                            p.data.unit_cost = cost
                            p.api.refreshCells({ rowNodes: [p.node], columns: ['unit_cost', 'subtotal'], force: true })
                            this.cdr.detectChanges()
                        }
                    })
                    return input
                }
            },
            {
                headerName: 'Cantidad',
                field: 'quantity',
                flex: 1,
                cellRenderer: (p: any) => {
                    const input = document.createElement('input')
                    input.type = 'number'
                    input.min = '1'
                    input.value = String(p.data.quantity ?? 1)
                    input.className = 'form-control form-control-sm text-center spare-qty-input'
                    input.addEventListener('change', () => {
                        const qty = parseInt(input.value, 10)
                        if (!isNaN(qty) && qty >= 1) {
                            p.data.quantity = qty
                            p.api.refreshCells({ rowNodes: [p.node], columns: ['subtotal'], force: true })
                            this.cdr.detectChanges()
                        } else {
                            input.value = String(p.data.quantity)
                        }
                    })
                    return input
                }
            },
            {
                headerName: 'Subtotal',
                field: 'subtotal',
                flex: 1,
                valueGetter: (p: any) => (p.data.unit_cost ?? 0) * (p.data.quantity ?? 1),
                valueFormatter: (p: any) => p.value != null ? `$${(+p.value).toFixed(2)}` : ''
            },
            {
                headerName: '¿Es de inventario?',
                field: 'in_stock',
                flex: 1,
                cellRenderer: (p: any) => {
                    const input = document.createElement('input')
                    input.type = 'checkbox'
                    input.className = 'form-check-input'
                    input.checked = p.data.in_stock ?? true
                    input.addEventListener('change', () => {
                        p.data.in_stock = input.checked
                    })
                    return input
                }
            },
            {
                headerName: 'Acciones',
                field: 'id',
                flex: 1,
                cellRenderer: (p: any) => {
                    const btn = document.createElement('button')
                    btn.type = 'button'
                    btn.className = 'btn btn-link text-danger p-0'
                    btn.innerHTML = '<i class="fas fa-trash-alt"></i>'
                    btn.onclick = () => {
                        this.selectedRepairs = this.selectedRepairs.filter(r => r.id !== p.data.id)
                        this.spareGridApi?.setRowData(this.selectedRepairs)
                    }
                    return btn
                }
            }
        ]
    }


    // --- Helpers ---

    get isCriticoOrReparacion(): boolean {
        const id = this.form?.get('priority_id')?.value
        const name = (this.priority.find((p: any) => String(p.id) === String(id))?.name ?? '').trim().toUpperCase()
        return name.includes('CRITICO') || name.includes('CRÍTICO') || name.includes('REPARACI')
    }

    get isPreventivo(): boolean {
        const id = this.form?.get('priority_id')?.value
        const name = (this.priority.find((p: any) => String(p.id) === String(id))?.name ?? '').trim().toUpperCase()
        return name.includes('PREVENTIVO')
    }

    private updateValidatorsForPriority(priorityId: any) {
        const selected = this.priority.find((p: any) => String(p.id) === String(priorityId))
        const name = (selected?.name ?? '').trim().toUpperCase()
        const isCritico = name.includes('CRITICO') || name.includes('CRÍTICO') || name.includes('REPARACI')
        const isPrev = name.includes('PREVENTIVO')

        const failureCtrl = this.form.get('maintenance_failure_id')
        const mainTypeCtrl = this.form.get('maintenance_type_id')

        if (isCritico) {
            failureCtrl?.setValidators([Validators.required])
            mainTypeCtrl?.setValidators([Validators.required])
        } else {
            failureCtrl?.clearValidators()
            failureCtrl?.setValue('')
            this.form.get('maintenance_failure_type_id')?.setValue('')
            this.form.get('maintenance_detail_id')?.setValue('')
            this.typesByFailure = []
            this.detailsByType = []
            mainTypeCtrl?.setValidators(isPrev ? [Validators.required] : [])
        }

        failureCtrl?.updateValueAndValidity()
        mainTypeCtrl?.updateValueAndValidity()
    }

    get isInternal(): boolean {
        const id = this.form?.get('maintenance_type_id')?.value
        const selected = this.maintenanceType.find((t: any) => String(t?.id) === String(id))
        return String((selected?.name ?? '')).trim().toUpperCase() === 'INTERNO'
    }

    get isExternal(): boolean {
        const id = this.form?.get('maintenance_type_id')?.value
        const selected = this.maintenanceType.find((t: any) => String(t?.id) === String(id))
        return String((selected?.name ?? '')).trim().toUpperCase() === 'EXTERNO'
    }

    openAddCategoryModal() {
        this.newCategoryName = ''
        this.showAddCategoryModal = true
    }

    confirmAddCategory() {
        if (!this.newCategoryName.trim()) return
        this.spinner.show()
        this.apiMaintenance.createRepairCategory({ name: this.newCategoryName.trim() }).subscribe({
            next: (resp: any) => {
                const newCat = resp?.data || { id: Date.now(), name: this.newCategoryName.trim() }
                this.repairCategories = [...this.repairCategories, newCat]
                this.newRepair.category = newCat.name
                this.showAddCategoryModal = false
                this.showCategoryToast = true
                setTimeout(() => { this.showCategoryToast = false }, 2500)
            },
            error: () => {
                Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo guardar la categoría' })
            },
            complete: () => this.spinner.hide()
        })
    }

    openAddFailureModal() {
        this.newFailure = { failure_name: '', type_name: '', detail_name: '' }
        this.showAddFailureModal = true
    }

    confirmAddFailure() {
        if (!this.newFailure.failure_name.trim() || !this.newFailure.type_name.trim() || !this.newFailure.detail_name.trim()) return
        this.spinner.show()
        this.apiMaintenance.createFailure(this.newFailure).subscribe({
            next: (resp: any) => {
                const { failure_id, failure_type_id, detail_id } = resp.data
                const newEntry = {
                    id: failure_id,
                    name: this.newFailure.failure_name,
                    active: true,
                    types: [{
                        id: failure_type_id,
                        name: this.newFailure.type_name,
                        maintenance_failure_id: failure_id,
                        active: true,
                        details: [{
                            id: detail_id,
                            name: this.newFailure.detail_name,
                            maintenance_failure_type_id: failure_type_id,
                            is_active: true
                        }]
                    }]
                }
                this.failerus = [...this.failerus, newEntry]
                this.onChangeFailure(String(failure_id))
                setTimeout(() => {
                    this.onChangeType(String(failure_type_id))
                    this.form.patchValue({ maintenance_detail_id: detail_id })
                }, 0)
                this.showAddFailureModal = false
            },
            error: () => Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo guardar la falla' }),
            complete: () => this.spinner.hide()
        })
    }

    onChangeFailure(value: string) {
        const id = value ? +value : null
        this.selectedFailureId = id
        const selected = this.failerus.find(f => f.id === id)
        this.typesByFailure = selected?.types || []
        this.selectedTypeId = null
        this.detailsByType = []
        this.form.patchValue({ maintenance_failure_id: id, maintenance_failure_type_id: '', maintenance_detail_id: '' })
        if (this.typesByFailure.length) {
            this.form.get('maintenance_failure_type_id')?.enable()
        } else {
            this.form.get('maintenance_failure_type_id')?.disable()
            this.form.get('maintenance_detail_id')?.disable()
        }
    }

    onChangeType(value: string) {
        const id = value ? +value : null
        this.selectedTypeId = id
        const selected = this.typesByFailure.find(t => t.id === id)
        this.detailsByType = selected?.details || []
        this.form.patchValue({ maintenance_failure_type_id: id, maintenance_detail_id: '' })
        if (this.detailsByType.length) {
            this.form.get('maintenance_detail_id')?.enable()
        } else {
            this.form.get('maintenance_detail_id')?.disable()
        }
    }

    ngOnDestroy(): void {
        if (this._clock) { clearInterval(this._clock); this._clock = undefined }
    }

    openRemoveModal(index: number) {
        this.indexToRemove = index
        this.showRemoveModal = true
    }

    confirmRemove() {
        if (this.indexToRemove === null) return
        const index = this.indexToRemove
        this.indexToRemove = null
        this.showRemoveModal = false
        this.removeFile(index)
    }

    cancelRemove() {
        this.indexToRemove = null
        this.showRemoveModal = false
    }

    removeFile(index: number) {
        if (index < 0 || index >= this.selectedFiles.length) return
        this.selectedFiles.splice(index, 1)
        const prev = this.selectedPreviews.splice(index, 1)[0]
        if (prev) URL.revokeObjectURL(prev)
        this.selectedFiles = [...this.selectedFiles]
        this.selectedPreviews = [...this.selectedPreviews]
        this.form.patchValue({ evidences: this.selectedFiles })
        this.form.get('evidences')?.updateValueAndValidity({ emitEvent: false })
    }

    clearFiles() {
        this.selectedPreviews.forEach(url => { if (url) URL.revokeObjectURL(url) })
        this.selectedPreviews = []
        this.selectedFiles = []
        this.form.patchValue({ evidences: [] })
        this.form.get('evidences')?.updateValueAndValidity({ emitEvent: false })
    }

    onSubmit() {
        if (this.form.invalid) { this.form.markAllAsTouched(); return }
        const raw = this.form.getRawValue()
        const { evidences, ...payload } = raw as any

        Object.keys(payload).forEach((k) => {
            if (k.endsWith('_id')) payload[k] = this.toInt(payload[k])
        })

        const formData = new FormData()

        Object.entries(payload).forEach(([k, v]) => {
            if (v === undefined || v === null) return
            if (Array.isArray(v) || typeof v === 'object') return
            formData.append(k, typeof v === 'boolean' ? (v ? '1' : '0') : String(v))
        })

        if (this.selectedRepairs.length > 0) {
            const repairPayload = this.selectedRepairs.map(r => ({
                repair_id: r.id > 1000000000000 ? null : r.id,
                name: r.name,
                category: r.category,
                unit_cost: r.unit_cost,
                quantity: r.quantity,
                cost: r.unit_cost * r.quantity,
                in_stock: r.in_stock ?? true
            }))
            formData.append('repair_request', JSON.stringify(repairPayload))
        }

        if (this.selectedMaterials.length > 0) {
            const materialPayload = this.selectedMaterials.map(m => ({
                material_id: m.id,
                cost: m.cost,
                in_stock: m.in_stock ?? true
            }))
            formData.append('material_request', JSON.stringify(materialPayload))
        }

        this.selectedFiles.forEach((file) => formData.append('files[]', file))

        this.submitting = true
        this.spinner.show()
        this.apiMaintenance.addMaintenance(formData, null).subscribe({
            next: () => {},
            error: (err: any) => {
                this.spinner.hide()
                console.error('Error creating maintenance request', err)
                Swal.fire({ icon: 'error', title: 'Error', text: 'Se ha presentado un error al crear la solicitud de mantenimiento' })
            },
            complete: () => {
                this.submitting = false
                this.spinner.hide()
                Swal.fire({ icon: 'success', title: 'Exitoso', text: 'Se ha creado la solicitud de mantenimiento de forma exitosa' }).then(() => {
                    this.router.navigate(['/maintenance/list'])
                })
            }
        })
    }

    onCancel() {
        this.location.back()
    }
}
